/**
 * InfinitSales - Instagram Direct Service
 *
 * Cliente da API do canal Instagram. Espelha o waBusinessService, com IGSID no
 * lugar de telefone.
 *
 * Diferencas de contrato que o front precisa respeitar:
 * - Nao existe "iniciar conversa": o cliente precisa mandar a primeira mensagem
 *   (limitacao do Instagram, nao escolha nossa). Por isso nao ha ensureChat.
 * - Nao existe template nem envio fora da janela de 24h: o backend recusa com
 *   code 'outside_window' e o front mostra o aviso no lugar do composer.
 */

import api from '../global/api';
import requestCache from '../global/requestCache';

const INSTAGRAM_BASE = '/api/instagram';

// Status da conexao: o shell do inbox E o NavSidebar (remontado a cada troca
// de rota) pediam o mesmo GET. Cache 60s + dedup em voo; o 404 (flag off ou
// sem conta) e o caminho normal e fica cacheado tambem, relancado a quem
// chama para manter o contrato (quem chama trata o 404). As mutacoes de conta
// abaixo invalidam a chave.
const STATUS_CACHE_KEY = 'instagram:status';
const STATUS_TTL_MS = 60_000;

class InstagramService {
  // ============================================
  // CONEXAO
  // ============================================

  async getStatus({ force = false } = {}) {
    const entry = await requestCache.cachedGet(
      STATUS_CACHE_KEY,
      async () => {
        try {
          const response = await api.get(`${INSTAGRAM_BASE}/status`);
          return { data: response.data };
        } catch (e) {
          if (e?.response?.status === 404) return { notFound: e };
          throw e;
        }
      },
      { ttl: STATUS_TTL_MS, force },
    );
    if (entry.notFound) throw entry.notFound;
    return entry.data;
  }

  async getAccount() {
    const response = await api.get(`${INSTAGRAM_BASE}/account`);
    return response.data;
  }

  /**
   * Conecta a conta no modo TESTE, com o token gerado no painel da Meta.
   * O backend descobre ig_user_id/username chamando /me com o proprio token.
   */
  async connectManual(accessToken, tokenExpiresAt = null) {
    const response = await api.post(`${INSTAGRAM_BASE}/account/connect-manual`, {
      access_token: accessToken,
      token_expires_at: tokenExpiresAt,
    });
    requestCache.invalidate(STATUS_CACHE_KEY);
    return response.data;
  }

  /**
   * Conecta via Business Login (OAuth de producao): troca o ?code= que a Meta
   * devolveu no redirect. O redirect_uri PRECISA ser o mesmo do authorize.
   */
  async connectOAuth(code, redirectUri) {
    const response = await api.post(`${INSTAGRAM_BASE}/account/connect-oauth`, {
      code,
      redirect_uri: redirectUri,
    });
    requestCache.invalidate(STATUS_CACHE_KEY);
    return response.data;
  }

  /** purge=true apaga o historico junto — irreversivel. */
  async disconnect(purge = false) {
    const response = await api.delete(`${INSTAGRAM_BASE}/account`, {
      params: { purge },
    });
    requestCache.invalidate(STATUS_CACHE_KEY);
    return response.data;
  }

  // ============================================
  // CONVERSAS
  // ============================================

  async getChats(limit = 50, offset = 0, extra = {}) {
    const params = { limit, offset };
    if (extra.since) params.since = extra.since;
    if (extra.created_from) params.created_from = extra.created_from;
    if (extra.created_to) params.created_to = extra.created_to;
    if (extra.unanswered) params.unanswered = true;
    if (extra.unread) params.unread = true;
    // Paridade D11 e D12: etapa e responsável filtrados no servidor.
    if (Array.isArray(extra.label_ids) && extra.label_ids.length) params.label_ids = extra.label_ids;
    if (extra.member_id) params.member_id = extra.member_id;
    // Busca DENTRO dos filtros (inbox_search_within_filters).
    if (extra.q) params.q = extra.q;
    if (extra.q && extra.search_messages) params.search_messages = true;
    // Repetível: ?cf=campo:valor. Formato de fio do motor core
    // (services/custom_field_search.py), igual nos 4 canais.
    if (Array.isArray(extra.custom_fields) && extra.custom_fields.length) {
      params.cf = extra.custom_fields
        .filter(({ field_id, value }) => field_id && value)
        .map(({ field_id, value }) => `${field_id}:${value}`);
    }
    const response = await api.get(`${INSTAGRAM_BASE}/chats`, { params, paramsSerializer: { indexes: null } });
    return response.data;
  }

  async searchChats(q, searchMessages = false, customFields = null) {
    const params = { q, search_messages: searchMessages };
    const cf = (customFields || [])
      .filter(({ field_id, value }) => field_id && value)
      .map(({ field_id, value }) => `${field_id}:${value}`);
    if (cf.length) params.cf = cf;
    const response = await api.get(`${INSTAGRAM_BASE}/chats/search`, { params, paramsSerializer: { indexes: null } });
    return response.data;
  }

  async getChat(chatId) {
    const response = await api.get(`${INSTAGRAM_BASE}/chats/${chatId}`);
    return response.data;
  }

  async markAsRead(chatId) {
    const response = await api.post(`${INSTAGRAM_BASE}/chats/${chatId}/read`);
    return response.data;
  }

  // ============================================
  // MENSAGENS
  // ============================================

  async getMessages(chatId, limit = 50, offset = 0, options = {}) {
    const response = await api.get(`${INSTAGRAM_BASE}/chats/${chatId}/messages`, {
      params: { limit, offset },
      signal: options.signal,
    });
    return response.data;
  }

  async sendMessage(chatId, message) {
    const response = await api.post(`${INSTAGRAM_BASE}/chats/${chatId}/messages`, {
      to: '',
      message,
    });
    return response.data;
  }

  async sendMedia(toIgsid, mediaUrl, mediaType) {
    const response = await api.post(`${INSTAGRAM_BASE}/send-media`, {
      to: toIgsid,
      media_url: mediaUrl,
      media_type: mediaType,
    });
    return response.data;
  }

  // ============================================
  // UTILS
  // ============================================

  /** Nome de exibicao: nome do perfil > @username > IGSID. */
  getChatDisplayName(chat) {
    if (!chat) return '';
    return chat.contact_name || (chat.contact_username ? `@${chat.contact_username}` : chat.contact_igsid) || '';
  }

  /** Handle com arroba, quando conhecido. */
  getHandle(chat) {
    if (!chat || !chat.contact_username) return '';
    return `@${chat.contact_username}`;
  }

  getMessagePreview(chat) {
    if (!chat) return '';
    return chat.last_message_text || '';
  }

  /**
   * A janela de 24h esta aberta? Calculado no cliente a partir de
   * last_inbound_at para decidir se mostra o composer ou o aviso — o backend
   * revalida no envio, este calculo e so de UI.
   *
   * Usa a MESMA folga de 5 minutos do backend (grace_minutes em
   * InstagramChatManager.window_state_from_chat). Sem ela havia uma faixa de 5
   * minutos em que a UI mostrava o composer habilitado e o envio era recusado —
   * o operador digitava a resposta e perdia o texto.
   */
  isWindowOpen(chat) {
    if (!chat || !chat.last_inbound_at) return false;
    const last = new Date(chat.last_inbound_at);
    if (isNaN(last.getTime())) return false;
    const janelaMs = 24 * 60 * 60 * 1000 - 5 * 60 * 1000;
    return Date.now() - last.getTime() < janelaMs;
  }

  formatTime(timestamp) {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Ontem';

    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }
}

const instagramService = new InstagramService();
export default instagramService;
